import React, { useEffect, useMemo, useState } from 'react';
import './configEngine.css';
import { adminAPI } from '../../../services/authService';
import ConfigPageRegistry from './ConfigPageRegistry';
import ConfigFormBuilder from './ConfigFormBuilder';
import ConfigFieldLibrary from './ConfigFieldLibrary';
import ConfigDropdownMasters from './ConfigDropdownMasters';
import ConfigRaBillMaster from './ConfigRaBillMaster';
import ConfigValidationRules from './ConfigValidationRules';
import ConfigKpiBuilder from './ConfigKpiBuilder';
import ConfigVersions from './ConfigVersions';
import ConfigAuditLog from './ConfigAuditLog';

const TABS = [
  { key: 'page-registry', label: 'Page Registry' },
  { key: 'form-builder', label: 'Form Builder' },
  { key: 'field-library', label: 'Field Library' },
  { key: 'dropdown-masters', label: 'Dropdown Masters' },
  { key: 'ra-bill-master', label: 'RA Bill Master' },
  { key: 'validation-rules', label: 'Validation & Conditional Rules' },
  { key: 'kpi-builder', label: 'KPI Builder' },
  { key: 'versions', label: 'Configuration Versions' },
  { key: 'audit', label: 'Configuration Audit Log' },
];

const ConfigEngineShell = ({ canAccess, initialTab = 'page-registry' }) => {
  const [tab, setTab] = useState(initialTab);
  const [flags, setFlags] = useState(null);
  const [selectedPageKey, setSelectedPageKey] = useState('');
  const [selectedFormId, setSelectedFormId] = useState(null);

  useEffect(() => {
    adminAPI.getConfigFlags()
      .then((res) => setFlags(res?.data?.flags || res?.data || {}))
      .catch(() => setFlags({ ENABLE_PAGE_FORM_CONFIG: false }));
  }, []);

  const enabled = flags?.ENABLE_PAGE_FORM_CONFIG !== false;

  const body = useMemo(() => {
    switch (tab) {
      case 'page-registry':
        return (
          <ConfigPageRegistry
            onConfigureForms={(pageKey, formId) => {
              setSelectedPageKey(pageKey);
              setSelectedFormId(formId || null);
              setTab('form-builder');
            }}
            onConfigureKpis={(pageKey) => {
              setSelectedPageKey(pageKey);
              setTab('kpi-builder');
            }}
          />
        );
      case 'form-builder':
        return <ConfigFormBuilder pageKey={selectedPageKey} formId={selectedFormId} onSelectForm={setSelectedFormId} />;
      case 'field-library':
        return <ConfigFieldLibrary />;
      case 'dropdown-masters':
        return <ConfigDropdownMasters />;
      case 'ra-bill-master':
        return <ConfigRaBillMaster />;
      case 'validation-rules':
        return <ConfigValidationRules />;
      case 'kpi-builder':
        return <ConfigKpiBuilder pageKey={selectedPageKey} />;
      case 'versions':
        return <ConfigVersions />;
      case 'audit':
        return <ConfigAuditLog />;
      default:
        return <ConfigPageRegistry />;
    }
  }, [tab, selectedPageKey, selectedFormId]);

  if (!canAccess) {
    return <div className="empty-state"><h3>Access denied</h3><p>Configuration permissions are required.</p></div>;
  }

  if (flags && !enabled) {
    return (
      <div className="empty-state">
        <h3>Configuration disabled</h3>
        <p>ENABLE_PAGE_FORM_CONFIG is off. Existing pages continue to use hard-coded forms.</p>
      </div>
    );
  }

  return (
    <div className="ce-shell">
      <div className="section-header">
        <div>
          <h2>Page, Form &amp; KPI Configuration</h2>
          <p className="ce-muted" style={{ margin: '6px 0 0' }}>
            Phase 2–7: presentation, custom fields, permissions, rules/dropdowns, KPI Builder, and wider page coverage (Correspondence, Drawings, Milestones, Projects, BG, RA Bills). Publish applies live form config; otherwise hard-coded UI is used.
          </p>
        </div>
      </div>
      <div className="ce-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`ce-tab ${tab === t.key ? 'is-active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {body}
    </div>
  );
};

export default ConfigEngineShell;
